import { Badge } from '../../components/ui/badge';
import type { CrmOnboardingClub, CrmOnboardingRun } from '../../types/crmOnboarding';
import { newClubIds } from '../../lib/crmOnboarding/status';

// The registry PREVIEW, as a human reads it before anything is committed.
//
// ⚠️⚠️ THE GROUP NAMES LEAD, AND THEY ARE THE LOUDEST THING ON THE CARD. The
// CLI's real safety is a person reading them: a wrong group id syncs another
// tenant's clubs under our brand and reports a clean success. A preview that
// buried them under a club table would be a worse tool than the terminal.
//
// Presentational only (no queries), so it is server-render tested without jsdom.

export const GROUP_NAME_WARNING =
  'If ANY name below is not what you expect, stop. A wrong group id syncs another tenant’s clubs under our brand and reports a clean success.';

function fmt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

export function RegistryPreviewView({
  run,
  registered,
}: {
  run: CrmOnboardingRun;
  /** The clubs already in the registry, so the preview can mark what is NEW. */
  registered: readonly CrmOnboardingClub[];
}) {
  const isCommit = run.kind === 'registry-commit';
  const header = (
    <p className="text-xs text-slate-500">
      {isCommit ? 'Registration' : 'Preview'} started {fmt(run.startedAt)} by {run.triggeredByEmail}
      {run.finishedAt ? ` · finished ${fmt(run.finishedAt)}` : ''}
    </p>
  );

  if (run.status === 'running') {
    return (
      <div className="space-y-2" data-testid="preview-running">
        {header}
        <p className="text-sm text-slate-700">
          {isCommit
            ? 'Registering on the kyck-crm-sync Job — this writes the clubs to the registry. It usually takes under a minute; this page refreshes itself.'
            : 'Running on the kyck-crm-sync Job — this reads GymSales and writes nothing. It usually takes under a minute; this page refreshes itself.'}
        </p>
      </div>
    );
  }

  const r = run.result;
  if (r === null) {
    return (
      <div className="space-y-2" data-testid="preview-no-result">
        {header}
        {run.error && <pre className="whitespace-pre-wrap text-sm text-red-700">{run.error}</pre>}
        {run.resultUnavailable && <p className="text-sm text-red-700">{run.resultUnavailable}</p>}
        {!run.error && !run.resultUnavailable && (
          <p className="text-sm text-red-700">The run finished without a readable result. Nothing is shown.</p>
        )}
      </div>
    );
  }

  const fresh = newClubIds(
    r.clubs.map((c) => c.companyId),
    registered,
  );

  return (
    <div className="space-y-5" data-testid={isCommit ? 'commit-result' : 'preview-result'}>
      {header}

      {run.warning && (
        <p className="rounded-md border-2 border-red-400 bg-red-50 p-3 text-sm font-semibold text-red-800">
          {run.warning}
        </p>
      )}

      <div className="rounded-md border-2 border-amber-400 bg-amber-50 p-4" data-testid="preview-groups">
        <h3 className="text-base font-semibold text-slate-900">
          Configured groups ({r.groupNames.length}) — read these before trusting the brand
        </h3>
        <p className="mt-1 text-sm text-slate-800">{GROUP_NAME_WARNING}</p>
        <ul className="mt-3 space-y-1">
          {r.groupNames.map((g) => (
            <li key={g.id} className="text-base text-slate-900">
              <span className="font-mono text-sm text-slate-600">group {g.id}</span>{' '}
              {g.name === null ? (
                <span className="font-semibold text-red-700">
                  name not returned — cannot confirm what this group is
                </span>
              ) : (
                <span className="font-semibold">{g.name}</span>
              )}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-slate-700">
          A group returning no clubs is a normal answer, not a fault: several are configured for
          engagements that have not started yet.
        </p>
      </div>

      {r.reachedPageCap && (
        <p className="text-sm font-semibold text-red-700">
          The run hit its page cap, so this club list may be INCOMPLETE. A club missing from a truncated
          list stops syncing. Do not trust this preview.
        </p>
      )}

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-900">
          {isCommit ? 'Clubs written' : 'Clubs that would be written'} — {r.clubs.length}
          {fresh.size > 0 ? ` (${fresh.size} new to the registry)` : ' (none new)'}
        </h3>
        {r.clubs.length === 0 ? (
          <p className="text-sm text-slate-600">None.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Club</th>
                  <th className="px-3 py-2 font-medium">GymSales id</th>
                  <th className="px-3 py-2 font-medium">Country · state</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Timezone</th>
                </tr>
              </thead>
              <tbody>
                {r.clubs.map((c) => (
                  <tr key={c.companyId} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      {c.name ?? '(no name)'}{' '}
                      {fresh.has(c.companyId) && <Badge variant="info">New</Badge>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{c.companyId}</td>
                    <td className="px-3 py-2">
                      {c.country ?? '—'} · {c.state ?? '—'}
                    </td>
                    <td className="px-3 py-2">{c.status ?? '—'}</td>
                    <td className="px-3 py-2">
                      {c.resolvedTimeZone ?? '—'}
                      {c.timeZoneMismatch && <p className="text-xs text-amber-700">⚠️ {c.timeZoneMismatch}</p>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {r.skipped.length > 0 && (
        <div>
          <h3 className="mb-1 text-sm font-semibold text-amber-800">
            Skipped — {r.skipped.length} club(s) would NOT be written
          </h3>
          <ul className="space-y-1 text-sm">
            {r.skipped.map((s, i) => (
              <li key={`${s.companyId ?? 'x'}-${i}`}>
                <span className="font-medium">{s.name ?? '(no name)'}</span> ({s.companyId ?? '?'}) — {s.reason}:{' '}
                {s.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      {r.clubs403.length > 0 && (
        <p className="text-sm text-slate-600">
          {r.clubs403.length} club(s) answered 403 (authenticated, no access) — a standing GymSales fact, not a
          failure: {r.clubs403.map((c) => c.name ?? c.companyId).join(', ')}.
        </p>
      )}

      {r.failures.length > 0 && (
        <div>
          <h3 className="mb-1 text-sm font-semibold text-red-700">Failures ({r.failures.length})</h3>
          <ul className="space-y-1 text-sm text-red-700">
            {r.failures.map((f, i) => (
              <li key={i}>
                [{f.scope}] {f.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-sm text-slate-600">
        {isCommit
          ? r.groupsCheck === 'matched'
            ? 'Written to the registry. The group ids were checked against the preview before anything was written. New clubs get their first pull on the next run.'
            : 'Written to the registry.'
          : 'This was a preview. Nothing was written to the registry.'}
      </p>
    </div>
  );
}
